import { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { paqueteService, Paquete } from '../../services/paqueteService';
import { paqueteService as paqueteServiceStats } from '../../services/paqueteService';
import { format } from 'date-fns';

const PaqueteDashboard = () => {
  const navigate = useNavigate();
  const [trackingSearch, setTrackingSearch] = useState('');

  // Obtener estadísticas
  const { data: estadisticas } = useQuery(
    'paquetes-estadisticas',
    () => paqueteServiceStats.getEstadisticas()
  );

  // Obtener paquetes recientes
  const { data: paquetesRecientes } = useQuery(
    ['paquetes-recientes'],
    () => paqueteService.getAll(1, 10)
  );

  // Obtener paquetes despachados recientes
  const { data: paquetesDespachados } = useQuery(
    ['paquetes-despachados'],
    () => paqueteService.getAll(1, 10, 'despachado')
  );

  // Calcular métricas del día
  const hoy = new Date();
  const paquetesHoy = paquetesRecientes?.data.paquetes.filter(p => {
    const fecha = new Date(p.fechaEntrada);
    return fecha.toDateString() === hoy.toDateString();
  }) || [];

  const paquetesPendientes = paquetesRecientes?.data.paquetes.filter(p =>
    p.estado === 'recibido_centro'
  ) || [];

  const paquetesAlerta = paquetesRecientes?.data.paquetes.filter(p =>
    p.diasAlmacenaje >= 7 && ['registrado', 'en_almacen', 'recibido_centro', 'en_servicio', 'preparado_salida'].includes(p.estado)
  ) || [];

  // Calcular ingresos del día (simplificado - en producción debería venir del backend)
  const ingresosHoy = paquetesHoy.reduce((sum, p) => {
    const monto = typeof p.montoAlmacenaje === 'string' ? parseFloat(p.montoAlmacenaje) : (p.montoAlmacenaje || 0);
    return sum + (isNaN(monto) ? 0 : monto);
  }, 0);

  // Datos para gráficos
  const datosPorEstado = estadisticas?.data.porEstado || [];
  const datosPorTipo = estadisticas?.data.porTipo || [];

  // Top 5 clientes (simplificado)
  const clientesCount: { [key: string]: number } = {};
  paquetesRecientes?.data.paquetes.forEach(p => {
    if (p.cliente?.nombre) {
      clientesCount[p.cliente.nombre] = (clientesCount[p.cliente.nombre] || 0) + 1;
    }
  });
  const topClientes = Object.entries(clientesCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const handleBuscarTracking = () => {
    if (trackingSearch.trim()) {
      navigate(`/paquetes?tracking=${trackingSearch}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard de Paquetes</h1>
          <p className="text-gray-600 mt-1">Vista general de la operación logística</p>
        </div>
        <div className="flex space-x-3">
          <input
            type="text"
            placeholder="Buscar por tracking..."
            value={trackingSearch}
            onChange={(e) => setTrackingSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleBuscarTracking()}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            onClick={handleBuscarTracking}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Buscar
          </button>
        </div>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Paquetes en Almacén</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {estadisticas?.data.total || 0}
              </p>
            </div>
            <div className="bg-blue-100 rounded-full p-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {paquetesHoy.length} registrados hoy
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pendientes de Procesar</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">
                {paquetesPendientes.length}
              </p>
            </div>
            <div className="bg-yellow-100 rounded-full p-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Estado: Recibido en centro
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Alertas (&gt;7 días)</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {paquetesAlerta.length}
              </p>
            </div>
            <div className="bg-red-100 rounded-full p-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Requieren atención
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ingresos del Día</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                ${ingresosHoy.toFixed(2)}
              </p>
            </div>
            <div className="bg-green-100 rounded-full p-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Almacenaje + Servicios
          </p>
        </div>
      </div>

      {/* Gráficos y tablas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de estados */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Paquetes por Estado</h2>
          <div className="space-y-3">
            {datosPorEstado.map((item) => {
              const total = estadisticas?.data.total || 1;
              const porcentaje = (item.cantidad / total) * 100;
              return (
                <div key={item.estado} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 capitalize">
                      {item.estado.replace('_', ' ')}
                    </span>
                    <span className="font-medium">{item.cantidad}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{ width: `${porcentaje}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top clientes */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Top 5 Clientes</h2>
          <div className="space-y-3">
            {topClientes.length > 0 ? (
              topClientes.map(([cliente, count], index) => (
                <div key={cliente} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold">
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-800">{cliente}</span>
                  </div>
                  <span className="text-primary-600 font-bold">{count} paquetes</span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No hay datos disponibles</p>
            )}
          </div>
        </div>
      </div>

      {/* Actividad reciente */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Últimos registrados */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Últimos Paquetes Registrados</h2>
            <button
              onClick={() => navigate('/paquetes')}
              className="text-primary-600 hover:text-primary-800 text-sm"
            >
              Ver todos →
            </button>
          </div>
          <div className="space-y-3">
            {paquetesRecientes?.data.paquetes.slice(0, 5).map((paquete) => (
              <div
                key={paquete.id}
                className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/paquetes?tracking=${paquete.numeroTracking}`)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{paquete.numeroTracking}</p>
                    <p className="text-sm text-gray-500">{paquete.destinatarioNombre}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    paquete.estado === 'despachado' ? 'bg-green-100 text-green-800' :
                    paquete.estado === 'en_almacen' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {paquete.estado.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {format(new Date(paquete.fechaEntrada), 'dd/MM/yyyy HH:mm')}
                </p>
              </div>
            ))}
            {(!paquetesRecientes?.data.paquetes || paquetesRecientes.data.paquetes.length === 0) && (
              <p className="text-gray-500 text-center py-4">No hay paquetes registrados</p>
            )}
          </div>
        </div>

        {/* Últimos despachados */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Últimos Paquetes Despachados</h2>
            <button
              onClick={() => navigate('/paquetes/salida')}
              className="text-primary-600 hover:text-primary-800 text-sm"
            >
              Ver todos →
            </button>
          </div>
          <div className="space-y-3">
            {paquetesDespachados?.data.paquetes.slice(0, 5).map((paquete) => (
              <div
                key={paquete.id}
                className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/paquetes?tracking=${paquete.numeroTracking}`)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{paquete.numeroTracking}</p>
                    <p className="text-sm text-gray-500">{paquete.destinatarioNombre}</p>
                    {paquete.numeroGuiaSalida && (
                      <p className="text-xs text-gray-400">Guía: {paquete.numeroGuiaSalida}</p>
                    )}
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Despachado
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-400">
                    {paquete.fechaSalidaReal
                      ? format(new Date(paquete.fechaSalidaReal), 'dd/MM/yyyy HH:mm')
                      : '-'}
                  </p>
                  <p className="text-xs font-medium text-primary-600">
                    ${(typeof paquete.montoAlmacenaje === 'string' ? parseFloat(paquete.montoAlmacenaje) : (paquete.montoAlmacenaje || 0)).toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
            {(!paquetesDespachados?.data.paquetes || paquetesDespachados.data.paquetes.length === 0) && (
              <p className="text-gray-500 text-center py-4">No hay paquetes despachados</p>
            )}
          </div>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/paquetes/entrada')}
            className="p-6 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-left"
          >
            <div className="text-2xl font-bold mb-2">+ Nuevo Paquete</div>
            <p className="text-primary-100 text-sm">Registrar entrada de paquete</p>
          </button>
          <button
            onClick={() => navigate('/paquetes/salida')}
            className="p-6 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-left"
          >
            <div className="text-2xl font-bold mb-2">📦 Despachar</div>
            <p className="text-green-100 text-sm">Procesar salida de paquetes</p>
          </button>
          <button
            onClick={() => navigate('/paquetes')}
            className="p-6 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-left"
          >
            <div className="text-2xl font-bold mb-2">📋 Ver Todos</div>
            <p className="text-gray-100 text-sm">Lista completa de paquetes</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaqueteDashboard;

